
import { v4 as uuidv4 } from "uuid";
import { CharacterFeatureSource } from "@dndModels/character_feature_source/CharacterFeatureSource";
import { 
  IStringNumOrStringHash 
} from "@models";
import { 
  Character, 
  CharacterFeatureBase,
  WeaponKeyword, 
  Attack, 
  PseudoItem,
  Item,
  BasicItem,
  Ammunition,
  Armor,
  Container,
  Weapon,
  MagicItemKeyword,
  ArmorType,
  Spell,
  DeckItem,
  CampaignDeck,
  GemItem,
  Recipe,
  Creature,
  CreatureDetails,
  WeaponKeywordUpgradeFeature,
  WeaponKeywordUpgradeFeature2,
  RollPlus,
  Limitation,
  SpellLimitation,
  CreatureLimitation,
  ItemLimitation,
  CharacterInfusion,
  CharacterInfusionForAlly,
  StoredSpell,
  UpgradableNumber,
} from "@dndModels";
import { AbilityScoreType, ActionType, AttackType, BookType, DamageType, EffectType, ItemType, RecipeComponentType } from "@dndModels/Types";
import { EFFECT_TYPES } from "@dndModels/Constants";

import CommonUtilities from "@utilities/common_utilities";

const common_util = CommonUtilities.getInstance();

export class CharacterItem extends CharacterFeatureSource {
  true_id = "";
  magic = false;
  _name = "";
  attuned = false;
  equipped = false;
  customizations: IStringNumOrStringHash = {};
  charges_used = 0;
  in_char_container_id = "";
  potion_level = -1;
  extra = "";
  extra_spell: Spell | null = null;
  _base_item_id = "";
  proficiency_override = false;
  // magic_item_keyword_ids: string[] = [];
  selected_rarity = -1;
  favorite = false;
  infusions: (CharacterInfusion | CharacterInfusionForAlly)[] = [];
  stored_spells: StoredSpell[] = [];
  spell_storing_spell: StoredSpell = new StoredSpell();

  constructor(obj?: any) {
    super(obj);
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      if (this.source_id === "") {
        this.source_id = this.true_id;
      }
      // obj.char_magic_item is from the old way.
      if (obj.char_magic_item) {
        this.sub_char_feature_sources.push(new CharacterBaseItem(obj.char_magic_item));
      } else if (obj.magic_item_id) {
        const cfs = new CharacterBaseItem();
        cfs.source_id = obj.magic_item_id;
        this.sub_char_feature_sources.push(cfs);
      } else if (obj.char_base_item) {
        this.sub_char_feature_sources.push(new CharacterBaseItem(obj.char_base_item));
      } else if (obj.base_item_id) {
        const cfs = new CharacterBaseItem();
        cfs.source_id = obj.base_item_id;
        this.sub_char_feature_sources.push(cfs);
      }
      if (obj.char_magic_item_keywords && obj.char_magic_item_keywords.length > 0) {
        obj.char_magic_item_keywords.forEach((keyword: any) => {
          this.sub_char_feature_sources.push(new CharacterMagicItemKeyword(keyword));
        });
      } else if (obj.magic_item_keyword_ids && obj.magic_item_keyword_ids.length > 0) {
        // Old version
        obj.magic_item_keyword_ids.forEach((id: any) => {
          const keyword = new CharacterMagicItemKeyword();
          keyword.source_id = id;
          this.sub_char_feature_sources.push(keyword);
        });
      }
      this._name = obj._name ? `${obj._name}` : "";
      this.attuned = obj.attuned ? obj.attuned : false;
      this.equipped = obj.equipped ? obj.equipped : false;
      this.customizations = obj.customizations ? obj.customizations : {};
      if (this.char_feature_bases.length === 0 && obj.features && obj.features.length > 0) {
        obj.features.forEach((o: any) => {
          const fb = new CharacterFeatureBase(o);
          fb.source_id = this.source_id;
          fb.source_name = this.name;
          fb.source_type = "Item";
          // fb.root_id = this.source_id;
          // fb.root_name = this.name;
          // fb.root_type = "Item";
          
          this.char_feature_bases.push(fb);
        });
      }
      this.charges_used = obj.charges_used ? obj.charges_used : (obj.charges ? obj.charges : 0);
      this.in_char_container_id = obj.in_char_container_id ? obj.in_char_container_id : "Equipment";
      this.potion_level = obj.potion_level ? +obj.potion_level : -1;
      this._base_item_id = obj.base_item_id ? obj.base_item_id : "";
      this.extra = obj.extra ? obj.extra : "";
      // this.magic_item_keyword_ids = obj.magic_item_keyword_ids ? obj.magic_item_keyword_ids : [];
      this.selected_rarity = obj.selected_rarity ? obj.selected_rarity : -1;
      this.favorite = obj.favorite ? obj.favorite : false;
      this.infusions = obj.infusions ? obj.infusions.map((o: any) => {
        return new CharacterInfusionForAlly(o);
      }) : [];
      this.stored_spells = obj.stored_spells ? obj.stored_spells.map((o: any) => new StoredSpell(o)) : [];
      this.spell_storing_spell = obj.spell_storing_spell ? new StoredSpell(obj.spell_storing_spell) : new StoredSpell();
    }
  }

  get source_true_id() {
    return this.true_id;
  }

  get base_item_id(): string {
    const cfs = this.char_base_item;
    if (cfs) {
      return cfs.source_id;
    }
    return this._base_item_id === "" ? this.source_id : this._base_item_id;
  }

  get base_item(): Item | null {
    const cfs = this.char_base_item;
    if (cfs && cfs.source instanceof Item) {
      return cfs.source;
    }
    return null;
  }

  get char_base_item(): CharacterBaseItem | null {
    const cfs = this.sub_char_feature_sources.filter(o => o instanceof CharacterBaseItem)[0];
    if (cfs instanceof CharacterBaseItem) {
      return cfs;
    }
    return null;
  }

  get char_magic_item_keywords(): CharacterMagicItemKeyword[] {
    return this.sub_char_feature_sources.filter(o => o instanceof CharacterMagicItemKeyword) as CharacterMagicItemKeyword[];
  }

  get magic_item_keyword_ids(): string[] {
    return this.char_magic_item_keywords.map(o => o.source_id);
  }

  get magic_item_keywords() {
    return this.char_magic_item_keywords.filter(o => o.source).map(o => o.source) as MagicItemKeyword[];
  }

  get the_effect_types(): EffectType[] {
    let effect_types: EffectType[] = [];
    if (this.base_item) {
      effect_types = this.base_item.the_effect_types;
    }
    for (const mik of this.magic_item_keywords) {
      effect_types = [...effect_types,...mik.the_effect_types];
    }
    if (this.extra_spell) {
      effect_types = [...effect_types,...this.extra_spell.effect_types];
    }

    return effect_types;
  }

  get the_attack_types(): AttackType[] {
    let attack_types: AttackType[] = [];
    if (this.base_item) {
      attack_types = this.base_item.the_attack_types;
    }
    for (const mik of this.magic_item_keywords) {
      attack_types = [...attack_types,...mik.the_attack_types];
    }
    if (this.extra_spell) {
      attack_types = [...attack_types,...this.extra_spell.attack_types];
    }

    return attack_types;
  }

  get the_utility_details(): string[] {
    let effect_types: string[] = [];
    if (this.base_item) {
      effect_types = this.base_item.the_utility_details;
    }
    for (const mik of this.magic_item_keywords) {
      effect_types = [...effect_types,...mik.the_utility_details];
    }
    if (this.extra_spell) {
      effect_types = [...effect_types,...this.extra_spell.the_utility_details];
    }

    return effect_types;
  }

  get pseudo_item_id(): string {
    let id = "";
    if (this.base_item) {
      id = "B:" + this.base_item._id + ",";
    }
    const k = this.char_magic_item_keywords.map(o => o.source_id).join(";");
    if (k !== "") {
      id += "K:" + k + ",";
    }
    if (this.potion_level > 0) {
      id += "L:" + this.potion_level + ",";
    }
    
    if (this.base_item_extra) {
      if (this.extra !== "") {
        id += "BIE:" + this.extra + ",";
      }
    } else if (this.extra !== "") {
      id += "E:" + this.extra + ",";
    }
    if (this.use_selected_rarity > -1) {
      id += `R:${this.use_selected_rarity},`;
    }

    return id;
  }

  get base_item_extra(): boolean {
    return this.base_item !== null && this.base_item.extra_base_type !== "" && this.base_item.extra_base_type !== "None";
  }

  get invert_charges() {
    if (this.base_item && this.base_item.charges > 0) {
      return this.base_item.invert_charges;
    }
    for (let i = 0; i < this.char_magic_item_keywords.length; ++i) {
      const keyword = this.char_magic_item_keywords[i];
      if (keyword.max_charges > 0) {
        return keyword.invert_charges;
      }
    }
    return false;
  }

  get max_charges(): number {
    if (this.base_item && this.base_item.charges > 0) {
      return this.base_item.charges;
    }
    for (let i = 0; i < this.char_magic_item_keywords.length; ++i) {
      const keyword = this.char_magic_item_keywords[i];
      if (keyword.max_charges > 0) {
        return keyword.max_charges;
      }
    }
    return 0;
  }

  get charge_refresh_rule() {
    if (this.base_item && this.base_item.charges > 0) {
      return this.base_item.charge_refresh_rule;
    } else {
      const cmik = this.char_magic_item_keywords.find(o => 
        o.source instanceof MagicItemKeyword && o.max_charges > 0);
      if (cmik && cmik.source instanceof MagicItemKeyword) {
        return cmik.source.charge_refresh_rule;
      }
    }
    return "Dawn";
  }

  get charge_refresh_rolls() {
    const roll_plus = new RollPlus();
    if (this.base_item && this.base_item.charges > 0) {
      roll_plus.count = this.base_item.refresh_dice_count;
      roll_plus.size = this.base_item.refresh_dice_size;
      roll_plus.flat = this.base_item.refresh_dice_flat;
    } else {
      const cmik = this.char_magic_item_keywords.find(o => 
        o.source instanceof MagicItemKeyword && o.max_charges > 0);
      if (cmik && cmik.source instanceof MagicItemKeyword) {
        roll_plus.count = cmik.source.refresh_dice_count;
        roll_plus.size = cmik.source.refresh_dice_size;
        roll_plus.flat = cmik.source.refresh_dice_flat;
      }
    }
    roll_plus.recalculate_string();
    return roll_plus;
  }

  get attunement(): boolean {
    return ((this.base_item && this.base_item.attunement) || 
      this.char_magic_item_keywords.filter(o => o.attunement).length > 0);
  }

  get_use_name(): string {
    if (this._name === "") {
      let name = "";
      const selected_rarity = this.use_selected_rarity;
      if (this.base_item) {
        name = this.base_item.useNameForRarity({ selected_rarity, extra: this.extra, extra_spell: this.extra_spell });
      }
      if (this.varying_mik) {
        const name_rarities = Object.keys(this.varying_mik.rarity_names);
        let name2 = "";
        if (name_rarities.length === 0) {
          name2 = `${this.varying_mik.name_formula}`;
          name2 = name2.replace("{selected_rarity}", `${selected_rarity}`);
        } else if (name_rarities.includes(`${selected_rarity}`)) {
          name2 = this.varying_mik.rarity_names[selected_rarity];
        } 
        name = name2.replace("{base_item.name}", name);
      }
      for (const keyword of this.flat_miks) {
        // Adamantine {base_item.name}
        const name2 = `${keyword.name_formula}`;
        name = name2.replace("{base_item.name}", name);
      }
      if (name.includes("{extra") && this.extra !== "") {
        if (this.extra_spell) {
          if (name.includes("{extra.name}")) {
            name = name.replace("{extra.name}", this.extra_spell.name);
          } else {
            name = name.replace("{extra}", this.extra_spell.name);
          }
        } else {
          name = name.replace("{extra}", this.extra);
        }
      }

      if (this.potion_level > 0) {
        name += ` ${this.potion_level}`;
      }
      
      return name;
    }
    return this._name;
  }

  get name(): string {
    return this.get_use_name();
  }

  set name(value: string) {
    this._name = value;
  }

  get cost(): number {
    let cost = -1;
    if (this.customizations.cost) {
      cost = +this.customizations.cost * this.count;
    } else if (this.base_item) {
      cost = this.base_item.cost * this.count;
    }
    return Math.round(cost*100)/100;
  }

  get weight(): number {
    return this.get_weight();
  }

  get_weight(log_it = false) {
    let weight = 0;
    if (log_it) {
      console.log(this.base_item);
      if (this.base_item) {
        console.log("found");
      }
    }
    if (this.customizations.weight) {
      weight = +this.customizations.weight;
    } else if (this.base_item) {
      weight = this.base_item.weight;
    } 
    weight *= this.count;
    return Math.round(weight*100)/100;
  }

  get count(): number {
    return 1;
  }

  get equippable(): boolean {
    if (this.attunement) {
      return true;
    } else {
      return [
        "Armor",
        "Weapon",
        // "Ammunition",
        "Artisan's Tools",
        // "Game Set",
        "Musical Instrument",
        "Potion",
        "Poison",
        "Dust",
        "Ring",
        // "Rod",
        "Scroll",
        "Staff",
        "Tool",
        // "Vehicle",
        "Wand",
        "Wondrous",
        "Shoe",
        "Cape/Cloak",
        "Gloves",
        "Headgear",
        "Necklace",
        "Belt",
        "Manual",
        // "Container",
        "Bracers",
        // "Gem",
        "Holy Symbol",
        "Arcane Focus",
        "Druidic Focus",
        // "Other",
        "Deck"
      ].includes(this.item_type) || this.char_magic_item_keywords.length > 0;
    }
  }

  get item_type(): ItemType {
    if (this.base_item) {
      return this.base_item.item_type;
    } else if (["Equipment","Storage"].includes(this.true_id)) {
      return "Container";
    }
    return "Custom";
  }

  get stackable(): boolean {
    if (this.base_item) {
      if (this.base_item instanceof BasicItem || this.base_item instanceof Weapon) {
        return this.base_item.stackable;
      } else if (this.base_item instanceof Ammunition || this.base_item instanceof GemItem) {
        return true;
      }
    }
    return false;
  }

  get bundle_size(): number {
    if (this.base_item) {
      if (this.base_item instanceof BasicItem || this.base_item instanceof Weapon) {
        return this.base_item.bundle_size;
      } else if (this.base_item instanceof Ammunition) {
        return this.base_item.bundle_size;
      }
    }
    return 0;
  }

  get ignore_proficiency_requirements(): boolean {
    return this.proficiency_req_options.includes("True None") || this.proficiency_req_options.includes("None") || (this.base_item !== null && this.base_item.ignore_proficiency_requirements);
  }

  get proficiency_req_options(): string[] {
    return this.char_magic_item_keywords.flatMap(o => {
      if (o.source) {
        return o.source.proficiency_req_options;
      }
      return [];
    });
  } 

  get proficiency_as_weapons(): string[] {
    return this.char_magic_item_keywords.flatMap(o => {
      if (o.source) {
        return o.source.proficiency_as_weapons;
      }
      return [];
    });
  }

  get weapon_mastery_override(): string {
    const cmik = this.char_magic_item_keywords.find(o => o.source && o.source.weapon_mastery_override !== "");
    if (cmik && cmik.source) {
      return cmik.source.weapon_mastery_override;
    }
    return "";
  } 

  get weapon_mastery_override_cmik_id(): string {
    const cmik = this.char_magic_item_keywords.find(o => o.source && o.source.weapon_mastery_override !== "");
    if (cmik && cmik.source) {
      return cmik.true_id;
    }
    return "";
  }

  get override_weapon_damage_type(): DamageType | "" {
    const override_weapon_damage_types = this.char_magic_item_keywords.map(o => o.override_weapon_damage_type);
    return override_weapon_damage_types.length > 0 ? override_weapon_damage_types[0] : "";
  }

  get varying_mik(): CharacterMagicItemKeyword | undefined {
    return this.char_magic_item_keywords.find(o => o.min_rarity !== o.max_rarity);
  }

  get extra_mik(): CharacterMagicItemKeyword | undefined {
    return this.char_magic_item_keywords.find(o => o.source && o.source.extra_base_type !== "None");
  }

  get flat_miks(): CharacterMagicItemKeyword[] {
    return this.char_magic_item_keywords.filter(o => o.min_rarity === o.max_rarity);
  } 

  get use_selected_rarity(): number {
    if (this.selected_rarity === -1 && this.base_item) {
      if (this.base_item.min_rarity !== this.base_item.max_rarity) {
        return this.base_item.min_rarity;
      }
      if (this.varying_mik) {
        return this.varying_mik.min_rarity;
      }
    }
    return this.selected_rarity;
  }

  get rarity(): number {
    let rarity = 0;
    if (this.base_item) { 
      // Can only have one part from base_item and miks that has varying rarities
      if (this.base_item.min_rarity !== this.base_item.max_rarity) { 
        // base_item has varying rarities
        rarity += this.use_selected_rarity;
      } else {
        rarity += this.base_item.min_rarity;
        if (this.varying_mik) {
          rarity += this.use_selected_rarity;
        }
      }
      for (const keyword of this.flat_miks) {
        if (keyword.min_rarity > -1) {
          rarity += keyword.min_rarity;
        }
      }
    }
    if (this.potion_level > 7) {
      rarity += 3; // Very-Rare
    } else if (this.potion_level > 5) {
      rarity += 2; // Rare
    } else if (this.potion_level > 3) {
      rarity += 1; // Uncommon
    }
    return rarity;
  }

  // get rarityOld(): number {
  //   let rarity = 0;
  //   if (this.selected_rarity > -1) {
  //     rarity += this.selected_rarity;
  //   } else if (this.base_item) {
  //     rarity += this.base_item.min_rarity;
  //   }
  //   this.char_magic_item_keywords.forEach(cmik => {
  //     if (cmik.source && cmik.source.rarity > 0) {
  //       rarity += cmik.source.rarity;
  //     }
  //   });
  //   return rarity;
  // }

  // get color(): string {
  //   let color = "inherit"; // Common
  //   switch (this.rarity) {
  //     case 1: color = "green"; break; // Uncommon
  //     case 2: color = "blue"; break; // Rare
  //     case 3: color = "purple"; break; // Very-Rare
  //     case 4: color = "orange"; break; // Legendary
  //     case 5: color = "brown"; break; // Artifact
  //   }
  //   return color;
  // }

  get spell_dc(): UpgradableNumber {
    let spell_dc = new UpgradableNumber();
    if (this.base_item) {
      spell_dc = this.base_item.spell_dc;
    }
    if (spell_dc.isBlank() && this.char_magic_item_keywords.length > 0) {
      const cmik = this.char_magic_item_keywords.find(o => !o.spell_dc.isBlank());
      if (cmik) {
        spell_dc = cmik.spell_dc;
      }
    }
    return spell_dc;
  }

  get spell_attack(): UpgradableNumber {
    let spell_attack = new UpgradableNumber();
    if (this.base_item) {
      spell_attack = this.base_item.spell_attack;
    }
    if (spell_attack.isBlank() && this.char_magic_item_keywords.length > 0) {
      const cmik = this.char_magic_item_keywords.find(o => !o.spell_attack.isBlank());
      if (cmik) {
        spell_attack = cmik.spell_attack;
      }
    }
    return spell_attack;
  }

  get spell_mod(): UpgradableNumber {
    let spell_mod = new UpgradableNumber();
    if (this.base_item) {
      spell_mod = this.base_item.spell_mod;
    }
    if (spell_mod.isBlank() && this.char_magic_item_keywords.length > 0) {
      const cmik = this.char_magic_item_keywords.find(o => !o.spell_mod.isBlank());
      if (cmik) {
        spell_mod = cmik.spell_mod;
      }
    }
    return spell_mod;
  }

  // get level(): number {
  //   let level = 0;
  //   if (this.magic_item) {
  //     level = this.magic_item.level;
  //   }
  //   if (level === 0 && this.char_magic_item_keywords.length > 0) {
  //     level = Math.max(...this.char_magic_item_keywords.map(o => o.level));
  //   }
  //   return level;
  // }

  get variant_group() {
    return this.base_item?.variant_group??[];
  }

  get _id() {
    return this.base_item?._id??"";
  }

  get use_name() {
    return this.base_item?.use_name??"";
  }

  toDBObj(): any {
    const feature_source_obj = super.toDBObj();
    // Changing these to be saved in sub_char_feature_sources, which is part of feature_source_obj
    // const char_magic_item_keywords: any[] = [];
    // this.char_magic_item_keywords.forEach(keyword => {
    //   char_magic_item_keywords.push(keyword.toDBObj());
    // });
    // if (this.name.includes("Scroll")) {
    //   debugger;
    // }
    return {
      ...feature_source_obj,
      true_id: this.true_id,
      // magic: this.magic,
      base_item_id: this.base_item_id,
      char_base_item: this.char_base_item ? this.char_base_item.toDBObj() : null,
      char_magic_item_keywords: this.char_magic_item_keywords.map(o => o.toDBObj()),
      _name: this._name,
      attuned: this.attuned,
      equipped: this.equipped,
      customizations: this.customizations,
      // count: this.count,
      charges_used: this.charges_used,
      in_char_container_id: this.in_char_container_id,
      level: this.potion_level,
      item_type: this.item_type,
      extra: this.extra,
      // magic_item_keyword_ids: this.magic_item_keyword_ids,
      selected_rarity: this.selected_rarity,
      favorite: this.favorite,
      infusions: this.infusions.filter(o => o instanceof CharacterInfusionForAlly).map(o => o.toDBObj()),
      stored_spells: this.stored_spells.map(o => o.toDBObj()),
      spell_storing_spell: this.spell_storing_spell.toDBObj()
    };
  }

  copyPseudoItem = async (
    char: Character, 
    copyMe: PseudoItem, 
  ) => {
    this.magic = true;
    this.source_id = this.true_id;
    if (copyMe.the_base_item && copyMe.the_base_item._id) {
      const char_base_item = new CharacterBaseItem();
      await char_base_item.copy_source({ char, feature_source: copyMe.the_base_item, item_id: this.true_id });
      this.sub_char_feature_sources.push(char_base_item);
    }
    // this.char_magic_item_keywords = [];
    copyMe.magic_item_keywords.forEach(keyword => {
      const char_keyword = new CharacterMagicItemKeyword();
      char_keyword.copy_source({ char, feature_source: keyword, item_id: this.true_id });
      if (keyword.extra_base_type !== "None") {
        char_keyword.copy_extra(copyMe.extra, copyMe.extra_spell);
        if (EFFECT_TYPES.includes(copyMe.extra)) {
          this.extra = copyMe.extra as EffectType;
        }
        this.extra_spell = copyMe.extra_spell;
      }
      this.sub_char_feature_sources.push(char_keyword);
      // this.magic_item_keyword_ids.push(keyword._id);
    });
    this.charges_used = 0;
    this.potion_level = copyMe.potion_level;
    this.selected_rarity = copyMe.selected_rarity;
  }

  connectBaseItem = async ({ 
    owner = null, 
    base_item,
    class_id = ""
  }: { 
    owner?: Character | null, 
    base_item: Item,
    class_id?: string 
  }) => {
    await this.connectBaseItem2({ owner, base_item, class_id });
  }

  connectBaseItem2 = async ({ 
    owner = null, 
    base_item,
    class_id = ""
  }: { 
    owner?: Character | null, 
    base_item: Item,
    class_id?: string 
  }) => {
    if (owner) {
      let cbi = this.char_base_item;
      if (!cbi) {
        cbi = new CharacterBaseItem();
        this.sub_char_feature_sources.push(cbi);
      }
      if (cbi) {
        // Used to pass class_id for root_id, might need adjusting
        await cbi.copy_source({ char: owner, feature_source: base_item, item_id: this.true_id });
      }
    } 
    this.all_char_feature_bases.filter(o => o.use_source_id === base_item._id).forEach(char_feature_base => {
      char_feature_base.class_id = class_id;
    });
  }

  connectMagicItemKeyword = async ({ 
    owner = null, 
    keyword,
    class_id = "",
    extra_spell = null,
    char_feature_id = ""
  }: { owner?: Character | null, 
      keyword: MagicItemKeyword,
      class_id?: string,
      extra_spell?: Spell | null,
      char_feature_id?: string 
    }) => {
    this.extra_spell = extra_spell;
    let cmik: CharacterMagicItemKeyword | null = null;
    if (owner) {
      const cmik_finder = this.char_magic_item_keywords.find(o => 
        o.source_id === keyword._id);
      if (!cmik_finder) {
        cmik = new CharacterMagicItemKeyword();
        this.sub_char_feature_sources.push(cmik);
      } else {
        cmik = cmik_finder;
      }
      if (cmik) {
        // Used to pass class_id for root_id, might need adjusting
        await cmik.copy_source({ char: owner, feature_source: keyword, item_id: this.true_id });
        if (keyword.extra_base_type !== "None") {
          cmik.copy_extra(this.extra, extra_spell);
        }
        cmik.char_feature_id = char_feature_id;
      }
    } 
    this.all_char_feature_bases.filter(o => o.use_source_id === keyword._id).forEach(char_feature_base => {
      char_feature_base.class_id = class_id;
    });
  }

  clone(drop_ids = false): CharacterItem {
    let clone: CharacterItem = new CharacterBasicItem();
    if (this instanceof CharacterAmmunition) {
      clone = new CharacterAmmunition();
    } else if (this instanceof CharacterArmor) {
      clone = new CharacterArmor();
    } else if (this instanceof CharacterContainer) {
      clone = new CharacterContainer();
    } else if (this instanceof CharacterWeapon) {
      clone = new CharacterWeapon();
    } else if (this instanceof CharacterCustomItem) {
      clone = new CharacterCustomItem();
    } else if (this instanceof CharacterDeck) {
      clone = new CharacterDeck();
    } else if (this instanceof CharacterGem) {
      clone = new CharacterGem();
    } else if (this instanceof CharacterBook) {
      clone = new CharacterBook();
    }
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterItem, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.magic = copyMe.magic;
    this._name = copyMe._name;
    this.attuned = copyMe.attuned;
    this.equipped = copyMe.equipped;
    this.customizations = copyMe.customizations;
    this.charges_used = copyMe.charges_used;
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
      this.in_char_container_id = copyMe.in_char_container_id;
    }
    this.potion_level = copyMe.potion_level;
    this.extra = copyMe.extra;
    this.extra_spell = copyMe.extra_spell;
    copyMe.char_magic_item_keywords.forEach(cmik => {
      if (cmik.source && cmik.source.extra_base_type !== "None") {
        cmik.copy_extra(this.extra, this.extra_spell);
      }
    });
    // this.char_magic_item_keywords = [];
    // copyMe.char_magic_item_keywords.forEach(cmik => {
    //   this.char_magic_item_keywords.push(cmik.clone(drop_ids));
    // });
    // this.magic_item_keyword_ids = [...copyMe.magic_item_keyword_ids];
    this.selected_rarity = copyMe.selected_rarity;
    this.favorite = copyMe.favorite;
    this.infusions = copyMe.infusions.map(o => o.clone());
    this.stored_spells = copyMe.stored_spells.map(o => o.clone());
    this.spell_storing_spell = copyMe.spell_storing_spell.clone();
  }
}

export class CharacterMagicItemKeyword extends CharacterFeatureSource {
  source: MagicItemKeyword | null = null;
  extra = "";
  extra_spell: Spell | null = null;
  char_feature_id = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      if (this.source_id === "" && obj.magic_item_keyword_id) {
        this.source_id = obj.magic_item_keyword_id;
      }
      if (this.char_feature_bases.length === 0 && 
        obj.features && obj.features.length > 0) {
        obj.features.forEach((o: any) => {
          const fb = new CharacterFeatureBase(o);
          fb.source_id = this.source_id;
          fb.source_name = this.name;
          fb.source_type = "Item";
          // fb.root_id = this.source_id;
          // fb.root_name = this.name;
          // fb.root_type = "Item";
          this.char_feature_bases.push(fb);
        });
      }
      this.extra = obj.extra ? obj.extra : "";
      this.char_feature_id = obj.char_feature_id ? obj.char_feature_id : "";
    }
  }

  get max_charges(): number {
    if (this.source) {
      return this.source.charges;
    } 
    return 0;
  }

  get invert_charges() {
    if (this.source) {
      return this.source.invert_charges;
    } 
    return false;
  }

  get attunement(): boolean {
    return this.source !== null && this.source.attunement;
  }

  get name_formula(): string {
    return this.source ? this.source.name_formula : "";
  }

  get rarity_names() {
    return this.source?.rarity_names??{};
  }

  get min_rarity() {
    return this.source?.min_rarity??-1;
  }

  get max_rarity() {
    return this.source?.max_rarity??-1;
  }

  // get bonus_damages() {
  //   if (this.source) {
  //     return this.source.bonus_damages;
  //   }
  //   return [];
  // }

  get spell_dc() {
    if (this.source) {
      return this.source.spell_dc;
    }
    return new UpgradableNumber();
  }

  get spell_attack() {
    if (this.source) {
      return this.source.spell_attack;
    }
    return new UpgradableNumber();
  }

  get spell_mod() {
    if (this.source) {
      return this.source.spell_mod;
    }
    return new UpgradableNumber();
  }

  get override_weapon_damage_type(): DamageType | "" {
    return this.source?.override_weapon_damage_type??"";
  }

  // get level() {
  //   return this.source ? this.source.level : 0;
  // }

  toDBObj(): any {
    const feature_source_obj = super.toDBObj();
    return {
      ...feature_source_obj,
      extra: this.extra,
      char_feature_id: this.char_feature_id,
    };
  }

  clone(drop_ids = false): CharacterMagicItemKeyword {
    const clone = new CharacterMagicItemKeyword();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterMagicItemKeyword, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.source = copyMe.source;
    this.extra = copyMe.extra;
    this.char_feature_id = copyMe.char_feature_id;
  }

  copy_extra(extra: string, extra_spell: Spell | null) {
    this.extra = extra;
    this.extra_spell = extra_spell;
  }
}

export class CharacterBaseItem extends CharacterFeatureSource {
  source: Item | null = null;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      if (this.source_id === "" && obj.magic_item_keyword_id) {
        this.source_id = obj.magic_item_keyword_id;
      }
      if (this.char_feature_bases.length === 0 && 
        obj.features && obj.features.length > 0) {
        obj.features.forEach((o: any) => {
          const fb = new CharacterFeatureBase(o);
          fb.source_id = this.source_id;
          fb.source_name = this.name;
          fb.source_type = "Item";
          // fb.root_id = this.source_id;
          // fb.root_name = this.name;
          // fb.root_type = "Item";
          this.char_feature_bases.push(fb);
        });
      }
    }
  }

  toDBObj(): any {
    const feature_source_obj = super.toDBObj();
    return {
      ...feature_source_obj,
    };
  }

  clone(drop_ids = false): CharacterBaseItem {
    const clone = new CharacterBaseItem();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterBaseItem, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.source = copyMe.source;
  }
}

export class CharacterBasicItem extends CharacterItem {
  _count = 1;
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this._count = obj.count !== undefined ? obj.count : 1;
    }
  }

  toDBObj(): any {
    const basic_item_obj = super.toDBObj();
    return {
      ...basic_item_obj, 
      count: this.count
    };
  }
  
  get count(): number {
    return this._count;
  }

  set count(value: number) {
    this._count = value;
  }

  clone(drop_ids = false): CharacterBasicItem {
    const clone = new CharacterBasicItem();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterBasicItem, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this._count = copyMe._count;
  }
}

export class CharacterAmmunition extends CharacterItem {
  _count = 1;
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this._count = obj.count !== undefined ? obj.count : 1;
    }
  }

  get ammunition_type() {
    if (this.base_item instanceof Ammunition) {
      return this.base_item.ammunition_type;
    }
    return "None";
  }

  get item_type(): ItemType {
    if (this.base_item) {
      return this.base_item.item_type;
    } 
    return "Ammunition";
  }

  toDBObj(): any {
    const basic_item_obj = super.toDBObj();
    return {
      ...basic_item_obj, 
      count: this.count
    };
  }
  
  get count(): number {
    return this._count;
  }

  set count(value: number) {
    this._count = value;
  }

  get weight(): number {
    return this.get_weight();
  }

  get_weight(log_it = false) {
    let weight = 0;
    if (log_it) {
      console.log(this.base_item);
      if (this.base_item) {
        console.log("found");
      }
    }
    if (this.customizations.weight) {
      weight = +this.customizations.weight;
    } else if (this.base_item) {
      weight = this.base_item.weight;
    }
    weight *= this.count;
    return Math.round(weight*100)/100;
  }

  clone(drop_ids = false): CharacterAmmunition {
    const clone = new CharacterAmmunition();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterAmmunition, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this._count = copyMe._count;
  }
}

export class CharacterArmor extends CharacterItem {
  get armor_type(): ArmorType | null {
    const base_item = this.base_item;
    if (base_item instanceof Armor && base_item.armor_type) {
      return base_item.armor_type;
    } else {
      return null;
    }
  }

  get dex_bonus_max(): boolean | null {
    return this.armor_type?.dex_bonus_max ?? null;
  }

  get stealth_disadvantage(): boolean {
    if (this.char_magic_item_keywords.find(o => o.source && o.source.ignore_stealth_disadvantage)) {
      return false;
    } else {
      const base_item = this.base_item;
      if (base_item instanceof Armor) {
        return base_item.use_stealth_disadvantage();
      }
      return this.armor_type?.stealth_disadvantage ?? false;
    }
  }

  get base_armor_class(): number {
    const base_item = this.base_item;
    if (base_item instanceof Armor) {
      return base_item.base_armor_class;
    }
    return 0;
  }

  get AC_override(): number | null {
    if (Object.keys(this.customizations).includes("AC_override")) {
      return this.customizations.AC_override as number;
    }
    return null;
  }

  set AC_override(value: number | null) {
    if (value === null) {
      delete this.customizations.AC_override;
    } else {
      this.customizations.AC_override = value;
    }
  }

  get AC_modifier(): number {
    if (Object.keys(this.customizations).includes("AC_modifier")) {
      return this.customizations.AC_modifier as number;
    }
    return 0;
  }

  set AC_modifier(value: number) {
    if (value === 0) {
      delete this.customizations.AC_modifier;
    } else {
      this.customizations.AC_modifier = value;
    }
  }

  get armor_class(): number {
    if (this.AC_override === null) {
      return this.base_armor_class + this.AC_modifier;
    } else {
      return this.AC_override;
    }
  }

  get armor_type_id(): string {
    const base_item = this.base_item;
    if (base_item instanceof Armor) {
      return base_item.armor_type_id;
    }
    return "";
  }

  get require_special_proficiency(): boolean {
    const base_item = this.base_item;
    if (base_item instanceof Armor) {
      return base_item.require_special_proficiency;
    }
    return false;
  }

  get item_type(): ItemType {
    if (this.base_item) {
      return this.base_item.item_type;
    } 
    return "Armor";
  }

  toDBObj(): any {
    const basic_item_obj = super.toDBObj();
    return {
      ...basic_item_obj
    };
  }

  clone(drop_ids = false): CharacterArmor {
    const clone = new CharacterArmor();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterArmor, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
  }
}

export class CharacterContainer extends CharacterItem {
  content_weight = 0;
  // carry_weight: number = 0;
  
  constructor(obj?: any) {
    super(obj);
    this.content_weight = 0;
    // this.carry_weight = 0;
  }
  
  get base_item_ids(): string[] {
    const base_item = this.base_item;
    if (base_item instanceof Container) {
      return base_item.base_item_ids;
    }
    return [];
  }
  
  get item_types(): string[] {
    const base_item = this.base_item;
    if (base_item instanceof Container) {
      return base_item.item_types;
    }
    return [];
  }

  get ignore_weight(): boolean {
    if (this.base_item instanceof Container) {
      return this.base_item.ignore_weight;
    }
    return false;
  }

  get item_type(): ItemType {
    if (this.base_item) {
      return this.base_item.item_type;
    } 
    return "Container";
  }

  toDBObj(): any {
    const basic_item_obj = super.toDBObj();
    return {
      ...basic_item_obj, 
      // content_weight: this.content_weight,
      // carry_weight: this.carry_weight
    };
  }

  get max_weight(): number {
    const base_item = this.base_item;
    if (base_item instanceof Container && 
      base_item.max_weight > 0) {
      return base_item.max_weight;
    }
    return -1;
  }

  clone(drop_ids = false): CharacterContainer {
    const clone = new CharacterContainer();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterContainer, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.content_weight = copyMe.content_weight;
  }
}

export class CharacterWeapon extends CharacterItem {
  attacks: Attack[] = [];
  use_ability_score: AbilityScoreType = "STR";
  weapon_keywords: WeaponKeyword[] = [];
  weapon_keyword_upgrades: WeaponKeywordUpgradeFeature[] = [];
  weapon_keyword_upgrade2s: WeaponKeywordUpgradeFeature2[] = [];
  range_override = 0;
  _count = 1;
  using_attack_types: AttackType[] = [];
  equipped_hand = "Any";
  changed_weapon_mastery_property_ids: string[] = [];
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this._count = obj.count !== undefined ? obj.count : 1;
      this.using_attack_types = obj.using_attack_types ? obj.using_attack_types : [];
      this.equipped_hand = obj.equipped_hand ? obj.equipped_hand : "Any";
    }
  }

  get attack_stat() {
    const attack = this.attacks.find(o => o.attack_stat !== "None");
    if (attack && attack.attack_stat !== "None") {
      return attack.attack_stat;
    }
    return "STR";
  }

  get reach(): number {
    const base_item = this.base_item;
    if (base_item instanceof Weapon) {
      return +base_item.reach;
    }
    return 0;
  }

  get range(): number {
    if (this.range_override) {
      return +this.range_override;
    } else if (this.customizations.range) {
      return +this.customizations.range;
    } else {
      const base_item = this.base_item;
      if (base_item instanceof Weapon) {
        return +base_item.range;
      }
    }
    return 0;
  }

  get range2(): number {
    if (this.customizations.range2) {
      return +this.customizations.range2;
    } else {
      const base_item = this.base_item;
      if (base_item instanceof Weapon) {
        return +base_item.range2;
      }
    }
    return 0;
  }

  get range_string(): string {
    const range = this.range;
    const range2 = this.range2;
    return range > 0 && range2 > 0 ? `${range}/${range2}` : (range > 0 ? `${range}` : (range2 > 0 ? `${range2}` : ""));
  }

  get base_weapon_keyword_ids() { 
    return this.base_item instanceof Weapon ? this.base_item.weapon_keyword_ids : [];
  }

  get weapon_type() {
    return this.base_item instanceof Weapon ? this.base_item.weapon_type : "None";
  }
  
  get count(): number {
    return this._count;
  }

  set count(value: number) {
    this._count = value;
  }

  get weapon_mastery_property_ids() {
    if (this.changed_weapon_mastery_property_ids.length > 0) {
      return this.changed_weapon_mastery_property_ids;
    } else if (this.base_item instanceof Weapon) {
      return [this.base_item.weapon_mastery_property_id];
    }
    return [];
  } 

  get action_type(): ActionType {
    return "Attack";
  }

  get action_cost(): number {
    return 1;
  }

  get uses_proficiency_id(): string {
    if (this.base_item instanceof Weapon) {
      return this.base_item.uses_proficiency_id;
    } else {
      return "";
    }
  }

  get ammunition_type() {
    if (this.base_item instanceof Weapon) {
      return this.base_item.ammunition_type;
    }
    return "None";
  }

  get item_type(): ItemType {
    if (this.base_item) {
      return this.base_item.item_type;
    } 
    return "Weapon";
  }

  get main_damage_type() {
    const attack = this.attacks.find(o => o.main_damage_type !== "Typeless");
    if (attack) {
      return attack.main_damage_type;
    }
    return "Typeless";
  }

  toDBObj(): any {
    const base_item_obj = super.toDBObj();
    return {
      ...base_item_obj, 
      count: this.count,
      using_attack_types: this.using_attack_types,
      equipped_hand: this.equipped_hand
    };
  }

  clone(drop_ids = false): CharacterWeapon {
    const clone = new CharacterWeapon();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterWeapon, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.attacks = [...copyMe.attacks];
    this.use_ability_score = copyMe.use_ability_score;
    this.weapon_keywords = [...copyMe.weapon_keywords];
    this.range_override = copyMe.range_override;
    this._count = copyMe._count;
    this.using_attack_types = copyMe.using_attack_types;
    this.equipped_hand = copyMe.equipped_hand;
  }
}

export class CharacterCustomItem extends CharacterItem {
  _cost = 0;
  _weight = 0;
  _notes = "";
  _description = "";
  _count = 1;
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this._cost = obj._cost ? obj._cost : 0;
      this._weight = obj._weight ? obj._weight : 0;
      this._notes = obj._notes ? obj._notes : (obj.notes ? obj.notes : "");
      this._description = obj.description ? obj.description : "";
      this._count = obj._count !== undefined ? obj._count : 1;
    }
  }

  toDBObj(): any {
    const basic_item_obj = super.toDBObj();
    return {
      ...basic_item_obj, 
      _cost: this._cost, 
      _weight: this._weight, 
      _name: this._name, 
      _notes: this._notes, 
      description: this.description
    };
  }

  get name(): string {
    return this._name;
  }

  set name(value: string) {
    this._name = value;
  }

  get description(): string {
    return this._description;
  }

  set description(value: string) {
    this._description = value;
  }

  get cost(): number {
    return this._cost;
  }

  set cost(value: number) {
    this._cost = value;
  }

  get weight(): number {
    return this._weight * this._count;
  }

  get count(): number {
    return this._count;
  }

  set count(value: number) {
    this._count = value;
  }

  clone(drop_ids = false): CharacterCustomItem {
    const clone = new CharacterCustomItem();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterCustomItem, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this._cost = copyMe._cost;
    this._weight = copyMe._weight;
    this._notes = copyMe._notes;
    this._description = copyMe._description;
    this._count = copyMe._count;
  }
}

export class CharacterDeck extends CharacterItem {
  campaign_deck_id = "";
  deck: CampaignDeck | null = null;

  cards: string[] = []; // true_ids from the DeckItem

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.campaign_deck_id = obj.campaign_deck_id ? obj.campaign_deck_id : "";
      this.cards = obj.cards ? obj.cards : [];
    }
  }

  get deck_item() {
    if (this.base_item instanceof DeckItem) {
      return this.base_item;
    }
    return null;
  }

  get deck_id() {
    return this.deck_item?.deck_id??"";
  }

  toDBObj(): any {
    const base_item_obj = super.toDBObj();
    return {
      ...base_item_obj, 
      campaign_deck_id: this.campaign_deck_id,
      cards: this.cards
    };
  }

  clone(drop_ids = false): CharacterDeck {
    const clone = new CharacterDeck();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterDeck, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.campaign_deck_id = copyMe.campaign_deck_id;
    this.deck = copyMe.deck;
    this.cards = copyMe.cards;
  }

  connectBaseItem = async ({ 
    owner = null, 
    base_item,
    class_id = ""
  }: { 
    owner?: Character | null, 
    base_item: Item,
    class_id?: string 
  }) => {
    await this.connectBaseItem2({ owner, base_item, class_id });
    if (base_item instanceof DeckItem && base_item.deck_id === "") {
      this.cards = common_util.shuffleList(base_item.cards.map(o => o.true_id)) as string[];
    }
  }
}

export class CharacterGem extends CharacterItem {
  gem_size_id = "";
  _count = 1;
  // _weight: number = 0;
  // _cost: number = 0;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      // console.log(obj);
      this.gem_size_id = obj.gem_size_id ? obj.gem_size_id : "";
      this._count = obj._count !== undefined ? obj._count : 1;
      // this.weight = obj.weight ? obj.weight : 0;
      // this.cost = obj.cost ? obj.cost : 0;
    }
  }

  get sizes() {
    return this.base_item instanceof GemItem ? this.base_item.sizes : [];
  }

  get size() {
    const sizes = this.sizes;
    if (this.sizes.length > 0) {
      let gem_size_id = this.gem_size_id;
      if (gem_size_id === "") {
        const min_weight = Math.min(...sizes.map(o => o.weight));
        const min_size = sizes.find(o => o.weight === min_weight);
        if (min_size) {
          gem_size_id = min_size.true_id;
        }
      }
      const size = sizes.find(o => o.true_id === gem_size_id);
      if (size) {
        return size;
      }
    }
    return { 
      true_id: "",
      name: "", 
      cost: this.base_item?.cost??0, 
      weight: this.base_item?.weight??0, 
      ac: 0,
      hp: 0,
      extraction: ""
    };
  }

  get cost() {
    let cost = -1;
    const size = this.size;
    if (this.customizations.cost) {
      cost = +this.customizations.cost * this.count;
    } else if (size) {
      cost = size.cost * this.count;
    } else if (this.base_item) {
      cost = this.base_item.cost * this.count;
    }
    return Math.round(cost*100)/100;
  }

  set cost(value: number) {
    this.customizations.cost = value;
  }

  get name() {
    const size = this.size;
    return super.get_use_name() + (size.true_id ? ` ${size.name}` : "");
  }

  get weight() {
    const size = this.size;
    if (size.true_id !== "") {
      return this.count * size.weight;
    }
    return this.get_weight();
  }

  set weight(value: number) {
    this.customizations.weight = value;
  }

  get count() {
    return this._count;
  }

  set count(value: number) {
    this._count = value;
  }

  toDBObj(): any {
    const base_item_obj = super.toDBObj();
    return {
      ...base_item_obj, 
      gem_size_id: this.gem_size_id,
      _count: this._count,
      // cost: this.customizations.cost ? +this.customizations.cost : 0,
      // weight: this.customizations.weight ? +this.customizations.weight : 0,
    };
  }

  clone(drop_ids = false): CharacterGem {
    const clone = new CharacterGem();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterGem, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.gem_size_id = copyMe.gem_size_id;
    this._count = copyMe._count;
    // this._weight = copyMe._weight;
  }
}

export class CharacterRecipe {
  true_id = "";
  recipe: Recipe = new Recipe();
  pseudo_item: PseudoItem = new PseudoItem();
  base_item_recipe_id = "";
  mik_recipe_ids: { [mik_id: string]: string } = {};
  
  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      // debugger;
      this.recipe = obj.recipe ? new Recipe(obj.recipe) : new Recipe();
      this.base_item_recipe_id = obj.base_item_recipe_id ? obj.base_item_recipe_id : "";
      this.mik_recipe_ids = obj.mik_recipe_ids ? obj.mik_recipe_ids : {};
    }
  }

  get item_id() {
    return this.recipe.item_id;
  }

  toDBObj(): any {
    return { 
      true_id: this.true_id,
      recipe: this.recipe.toDBObj(),
      base_item_recipe_id: this.base_item_recipe_id,
      mik_recipe_ids: this.mik_recipe_ids,
    };
  }

  clone(): CharacterRecipe {
    const clone = new CharacterRecipe();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: CharacterRecipe): void {
    this.true_id = copyMe.true_id;
    this.recipe = copyMe.recipe.clone();
    this.pseudo_item = copyMe.pseudo_item;
    this.base_item_recipe_id = copyMe.base_item_recipe_id;
    this.mik_recipe_ids = copyMe.mik_recipe_ids;
  }
}

export class CharacterRecipeComponent {
  true_id = "";
  component_type: RecipeComponentType = "None";
  cr = 0;
  name = "";
  description = "";
  creature: Creature | undefined = undefined;
  creature_details: CreatureDetails = {
    creature_type: "",
    subtypes: [],
    age_category: "",
    size_num: 0,
    min_challenge_rating: 0,
    max_challenge_rating: 0,
    flying: false,
    swimming: false,
    _id: ""
  }

  get use_name() {
    return this.description === "" ? `${ this.name } from ${ this.creature?.name??"" } (CR: ${ this.cr })` : `${this.description} (CR: ${ this.cr })`;
  }

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.component_type = obj.component_type ? obj.component_type : "None";
      this.cr = obj.cr ? obj.cr : 0;
      this.name = obj.name ? obj.name : "";
      this.description = obj.description ? obj.description : "";
      this.creature_details = obj.creature_details ? obj.creature_details : {
        creature_type: "",
        subtypes: [],
        age_category: "",
        size_num: 0,
        min_challenge_rating: 0,
        max_challenge_rating: 0,
        flying: false,
        swimming: false,
        _id: ""
      };
      if (obj.creature_details && obj.creature_details.subtype) {
        this.creature_details.subtypes = [obj.creature_type.subtype];
        delete (this.creature_details as any).subtype;
      }
      if (this.creature_details.age_category === undefined) {
        this.creature_details.age_category = "";
      }
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      component_type: this.component_type,
      cr: this.cr,
      name: this.name,
      description: this.description,
      creature_details: this.creature_details
    };
  }

  clone(drop_ids = false): CharacterRecipeComponent {
    const new_obj = new CharacterRecipeComponent();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: CharacterRecipeComponent, drop_ids = false): void {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this.component_type = copyMe.component_type;
    this.cr = copyMe.cr;
    this.creature = copyMe.creature;
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.creature_details = copyMe.creature_details;
  }
}

export class CharacterBook extends CharacterItem {
  book_type: BookType = "Other";
  limit: Limitation = new Limitation();
  text = "";
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      // console.log(obj);
      this.book_type = obj.book_type ? obj.book_type : "Other";
      switch (this.book_type) {
        case "Spell":
          this.limit = obj.limit ? new SpellLimitation(obj.limit) : new SpellLimitation();
        break;
        case "Creature":
          this.limit = obj.limit ? new CreatureLimitation(obj.limit) : new CreatureLimitation();
        break;
        case "Item":
          this.limit = obj.limit ? new ItemLimitation(obj.limit) : new ItemLimitation();
        break;
      }
      this.text = obj.text ? obj.text : "";
    }
  }

  toDBObj(): any {
    const basic_item_obj = super.toDBObj();
    return {
      ...basic_item_obj, 
      book_type: this.book_type,
      limit: this.limit.toDBObj(),
      text: this.text
    };
  }

  clone(drop_ids = false): CharacterBook {
    const clone = new CharacterBook();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterBook, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.book_type = copyMe.book_type;
    this.limit = copyMe.limit.clone();
    this.text = copyMe.text;
  }
}